/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useEmailConfigData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading email config data for edit/duplicate modes.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { fetchEmailConfig } from '../api/emailSettingsApi';
import type { EmailConfig } from '../types';

export function useEmailConfigData(
  id: string | undefined,
  duplicateId: string | null,
  mode: 'create' | 'edit',
  resetParam?: string | null
) {
  const { t } = useI18n();
  const { showError, showInfo } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const configLoadedRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);
  
  // Use refs for functions to avoid re-renders
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);
  
  // Update refs when functions change
  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showError, showInfo]);

  // Reload function that can be called externally
  const reload = useCallback(async () => {
    const targetId = mode === 'edit' ? id : duplicateId;
    if (!targetId || targetId === 'new') {
      return null;
    }

    if (isLoadingRef.current) {
      return null;
    }

    configLoadedRef.current = targetId;
    isLoadingRef.current = true;
    setLoading(true);

    try {
      const data = await fetchEmailConfig(targetId, tenantSlug, user?.id ? Number(user.id) : undefined);
      setConfig(data);
      return data;
    } catch (error) {
      showErrorRef.current(t('emailSettings.messages.loadError'));
      return null;
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [id, duplicateId, mode, tenantSlug, user?.id, t]);

  useEffect(() => {
    const loadConfig = async () => {
      const targetId = mode === 'edit' ? id : duplicateId;
      const currentId = id || 'new';
      const previousId = lastIdRef.current;
      const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
      const hasResetParam = !!resetParam && resetParam !== resetParamProcessedRef.current;
      
      // If reset param is present and not yet processed, force reset
      if (hasResetParam) {
        formResetRef.current = false;
        configLoadedRef.current = null;
        // Clear the lastIdRef to ensure we detect the change
        lastIdRef.current = undefined;
        // Mark this reset param as processed
        resetParamProcessedRef.current = resetParam;
      }

      if (!targetId || targetId === 'new') {
        if (mode === 'create') {
          // Reset form when navigating to new (either initial load or after save)
          if (hasResetParam || !formResetRef.current || idChangedToNew || configLoadedRef.current === null) {
            formResetRef.current = true;
            lastIdRef.current = currentId;
            configLoadedRef.current = 'create';
            setConfig(null);
          } else {
            lastIdRef.current = currentId;
          }
        }
        return;
      }

      // Prevent duplicate loads
      if (configLoadedRef.current === targetId && !hasResetParam && !idChangedToNew) {
        lastIdRef.current = currentId;
        return;
      }

      if (isLoadingRef.current) {
        return;
      }

      configLoadedRef.current = targetId;
      isLoadingRef.current = true;
      setLoading(true);

      try {
        const data = await fetchEmailConfig(targetId, tenantSlug, user?.id ? Number(user.id) : undefined);
        setConfig(data);
        formResetRef.current = false;
        lastIdRef.current = currentId;

        // Show duplicate hint notification
        if (duplicateId) {
          showInfoRef.current(t('emailSettings.messages.duplicateHint'));
        }
      } catch (error) {
        showErrorRef.current(t('emailSettings.messages.loadError'));
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, duplicateId, tenantSlug, user?.id, resetParam]);

  return {
    config,
    loading,
    reload,
  };
}

